<template>
  <div class="relative flex pr-6 mx-0 overflow-auto dark:bg-dark-screen-background bg-light-secondary-screen-background">
    <div v-if="!component_record && !record_error" class="text-common-grey">
      <div>{{ $t("Loading...") }}</div>
    </div>
    <div v-if="record_error" class="text-common-grey">
      <div>{{ $t("Record not found") }}</div>
    </div>

    <Menu as="div" class="absolute flex-shrink-0 top-4 right-10">
      <MenuButton
        class="inline-flex items-center justify-center w-5 h-5 text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
        <EllipsisVertical class="w-5 h-5" aria-hidden="true" />
      </MenuButton>
      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0">
        <MenuItems
          class="absolute z-50 mx-3 -ml-[250px] whitespace-nowrap origin-bottom-right dark:bg-dark-screen-background w-80 mt-1 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
          <MenuItem v-slot="{ active }">
            <div
              :class="[
                active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                ' cursor-pointer group flex items-center px-4 py-2 '
              ]"
              @click="switchLayoutFields(wide_fields)">
              <template v-if="!wide_fields">
                <SquareStack class="w-5 h-5 mr-2" /> {{ $t("Show 3 per row") }}
              </template>
              <template v-else>
                <Grid2x2 class="w-5 h-5 mr-2" /> {{ $t("Show 2 per row") }}
              </template>
            </div>
          </MenuItem>

          <MenuItem v-slot="{ active }">
            <div
              :class="[
                active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                ' cursor-pointer group flex items-center px-4 py-2 '
              ]"
              @click="
                $emitter.emit(ALL_EMITTERS.UPDATE_METRICS, {
                  page_reference: page_ref,
                  object_type: component_record.type,
                  view_type: VIEW_TYPES.TABLE
                })
              ">
              <ChartNoAxesColumnDecreasing class="mr-2" /> {{ $t("Add/Remove Overview Metric") }}
            </div>
          </MenuItem>

          <MenuItem v-slot="{ active }">
            <div
              :class="[
                active ? 'bg-gray-100 dark:bg-dark-screen-background text-gray-400' : 'text-gray-700 dark:text-dark-body-text',
                ' cursor-pointer group flex items-center px-4 py-2 '
              ]"
              @click="
                $emitter.emit('createCustomMetric', {
                  page_reference: page_ref,
                  object_type: component_record.type,
                  view_type: VIEW_TYPES.TABLE
                })
              ">
              <ChartNoAxesColumnDecreasing class="mr-2" /> {{ $t("Add Custom Metric") }}
            </div>
          </MenuItem>
        </MenuItems>
      </transition>
    </Menu>

    <div v-if="component_record" class="mt-[0px] border-gray-200 flex-grow mb-0">
      <div class="sm:divide-gray-200">
        <div
          class="px-2 text-dark transition duration-side-menu-transition-duration dark:text-white font-bold text-[16px] has-tooltip cursor-default">
          {{ label }}
          <span
            class="absolute top-0 left-0 inline-flex items-center gap-2 px-2 py-1 text-sm font-medium rounded-md tooltip z-50 bg-light-screen-background dark:bg-dark-screen-background dark:text-dark-body-text">
            {{ $t("For") }}: {{ for_label }}
          </span>
        </div>
        <Form
          v-slot="{ validate }"
          :validation-schema="schema"
          :initial-values="initial_values"
          class="grid grid-cols-6 dark:divide-dark-divider-border">
          <div
            v-for="metric in record_metrics"
            :key="metric.id"
            :class="[!wide_fields ? '!col-span-3' : '!col-span-2']"
            class="flex items-start content-center px-2 py-4 align-center">
            <dd
              class="relative flex w-full mt-1 text-gray-900 sm:text-base"
              @click.stop="handleClick(metric, $event)"
              @mouseover="handleMouseOver($event)"
              @mouseout="handleMouseOut($event)">
              <MetricFieldInput
                v-model:metric-value="metric.value"
                :disabled="editing_disabled"
                class="w-full ml-auto"
                :update-value="true"
                :show-label="true"
                :validate-on-input="false"
                :show-success-check-mark="true"
                :hide-success-check-mark-after="800"
                :record="component_record"
                :label="'Input'"
                :metric="metric"
                :component-reference="componentReference"
                @update:metric-value="($event) => handleUpdateMetricValue(validate)" />
            </dd>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script>
import { useLayoutItemSharedLogic } from "@/components/shared/pages/useLayoutItemSharedLogic.js"
import * as Yup from "yup"
import { Form } from "vee-validate"
import MetricFieldInput from "@/components/shared/MetricFieldInput.vue"
import { VIEW_TYPES } from "@/store/constants/references"
import { EllipsisVertical, SquareStack, Grid2x2, ChartNoAxesColumnDecreasing } from "lucide-vue-next"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { utils } from "@/store/helpers"

export default {
  name: "CompanyOverviewTab",
  components: {
    ChartNoAxesColumnDecreasing,
    MetricFieldInput,
    Form,
    EllipsisVertical,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Grid2x2,
    SquareStack
  },
  inject: ["getViewMetrics", "setViewMetrics", "canUserEditLayout"],
  props: {
    label: null,
    linkedRecord: {
      type: String,
      required: false
    },
    connectTo: {
      type: String,
      required: false
    },
    currentCompany: {
      type: Object,
      required: false
    },
    componentReference: {
      type: String,
      required: false
    },
    id: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      ALL_EMITTERS,
      VIEW_TYPES,
      loading: null,
      wide_fields: false,
      record_metrics: [],
      schema: {},
      initial_values: {},
      overview_metrics: [],
      component_record: null,
      record_error: false
    }
  },

  computed: {
    for_label() {
      return this.component_record
        ? this.connectTo == "this_record"
          ? `
          ${this.record_error ? this.$t("Not found") : utils.getRecordName(this.component_record)}`
          : `${this.record_error ? this.$t("Record not found") : utils.getRecordName(this.component_record)}`
        : this.record_error
          ? this.$t("Record Not Found")
          : this.$t("All Records")
    },
    page_ref() {
      if (this.$route.name == "ObjectProfile") {
        if (this.$route.params.object_type == "contact") {
          return "contact_profile"
        }
        return "object_profile"
      } else if (this.$route.name == "CompanyProfile") {
        return "company_profile"
      }
      return this.id
    },
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },
  watch: {
    overview_metrics() {
      this.getRecordMetrics()
    }
  },

  created() {
    this.wide_fields = this.$store.state.ui_options?.wide_fields ? this.$store.state.ui_options.wide_fields : false
    const { loading, record_error, component_record } = useLayoutItemSharedLogic(
      this.$props,
      this.postGetComponentRecord,
      this.registerListeners,
      this.unregisterListeners
    )
    this.loading = loading
    this.record_error = record_error
    this.component_record = component_record

    this.registerListeners()
  },
  unmounted() {
    this.unregisterListeners()
  },
  methods: {
    switchLayoutFields(wide_fields) {
      this.$store.dispatch("ui/setUIValue", {
        wide_fields: !this.wide_fields
      })
      this.wide_fields = !wide_fields
    },
    handleMouseOver(event) {
      if (event.ctrlKey || event.altKey) {
        event.target.classList.add("cursor-pointer", "underline", "text-blue")
      } else {
        event.target.classList.remove("cursor-pointer", "underline", "text-blue")
      }
    },
    handleMouseOut(event) {
      event.target.classList.remove("cursor-pointer", "underline", "text-blue")
    },
    handleClick($event, metric) {
      this.editing_disabled ? null : $event?.ctrlKey || $event?.altKey ? this.openEditMetric(metric) : void null
    },
    handleUpdateMetricValue() {
      this.$emitter.emit(
        ALL_EMITTERS.REFRESH_ROLLUP_METRICS,
        this.linkedRecord ? this.linkedRecord : this.$store.state.ui.currentCompany.id
      )
    },
    registerListeners() {
      this.$emitter.on(ALL_EMITTERS.RECORD_UPDATED, (record) => {
        if (this.connectTo == "another_record" && record.id == this.linkedRecord) {
          this.component_record = record
        }
      })

      this.$emitter.on(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_OVERVIEW_COMPONENT, () => {
        this.overview_metrics = this.getViewMetrics({
          page_reference: this.page_ref,
          object_type: this.component_record?.type,
          view_type: VIEW_TYPES.TABLE
        })
      })
      this.$emitter.on(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_OVERVIEW_COMPONENT, (metric) => {
        let new_record_metrics = this.$clone(this.record_metrics)
        new_record_metrics.push(metric)
        this.setViewMetrics({
          page_reference: this.page_ref,
          object_type: this.component_record?.type,
          view_type: VIEW_TYPES.TABLE,
          new_metrics_list: new_record_metrics.map((x) => x.reference_name)
        })

        setTimeout(() => {
          this.overview_metrics = this.getViewMetrics({
            page_reference: this.page_ref,
            object_type: this.component_record?.type,
            view_type: VIEW_TYPES.TABLE
          })
        }, 400)
      })
      this.$emitter.on(ALL_EMITTERS.RECORD_UPDATED_IN_OVERVIEW_COMPONENT, (data) => {
        if (data.id == this.component_record?.id && data.id == this.$store.state.ui.currentCompany?.id) {
          this.$store.commit("data/setRecord", data)
          this.$store.dispatch("ui/setCurrentCompany", data)
          this.component_record = data

          this.overview_metrics = this.getViewMetrics({
            page_reference: this.page_ref,
            object_type: this.component_record?.type,
            view_type: VIEW_TYPES.TABLE
          })
        } else if (data.id == this.component_record?.id) {
          this.component_record = data
        }
      })
    },
    unregisterListeners() {
      this.$emitter.off(ALL_EMITTERS.CURRENT_COMPANY_UPDATED_IN_OVERVIEW_COMPONENT)
      this.$emitter.off(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_OVERVIEW_COMPONENT)
      this.$emitter.off(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_OVERVIEW_COMPONENT)
      this.$emitter.off(ALL_EMITTERS.RECORD_UPDATED_IN_OVERVIEW_COMPONENT)
      this.$emitter.off(ALL_EMITTERS.METRICS_CHANGED)
    },

    postGetComponentRecord() {
      this.overview_metrics = this.getViewMetrics({
        page_reference: this.page_ref,
        object_type: this.component_record?.type,
        view_type: VIEW_TYPES.TABLE
      })
      this.getRecordMetrics()
    },
    getComponentRecord() {
      utils.getComponentRecord(this.$props).then((record) => {
        this.component_record = record
        this.postGetComponentRecord()
      })
    },

    prepareValidationSchema() {
      this.schema = {}
      this.initial_values = {}

      this.record_metrics?.forEach((metric) => {
        this.initial_values[metric.reference_name] = this.component_record.values[metric.reference_name]
        if (metric.data_type == "Json") {
          this.initial_values[metric.reference_name] = this.component_record.values[metric.reference_name]?.name
        }
        if (metric.input_type == "URL") {
          this.schema[metric.reference_name] = Yup.string()
            .matches(
              /^(http[s]?:\/\/(www\.)?|ftp:\/\/(www\.)?|www\.){0,1}([0-9A-Za-z-\\.@:%_\\+~#=]+)+((\.[a-zA-Z]{2,3})+)(\/(.)*)?(\?(.)*)?/,
              this.$t("Please enter a valid url address")
            )
            .nullable()
        } else if (metric.data_type == "Number") {
          this.schema[metric.reference_name] = Yup.string()
            .matches(/^[0-9-.]+$/, this.$t("Please enter a valid number"))
            .nullable()
        } else if (metric.input_type == "Email") {
          this.schema[metric.reference_name] = Yup.string().email(this.$t("Please enter a valid email address")).nullable()
        } else {
          this.schema[metric.reference_name] = Yup.string().nullable()
        }
      })
    },
    openEditMetric(metric) {
      if (metric.metric_class == "computed_metric") {
        this.$emitter.emit(ALL_EMITTERS.EDIT_COMPUTED_METRIC, { metric: metric })
      } else {
        this.$emitter.emit(ALL_EMITTERS.EDIT_CUSTOM_METRIC, { metric: metric })
      }
    },
    getRecordMetrics() {
      this.record_metrics = []
      this.overview_metrics.forEach((metric_ref) => {
        const click_to_edit = !(
          this.$utils.getMetricByReferenceName(metric_ref).input_type == "StarRating" ||
          this.$utils.getMetricByReferenceName(metric_ref).input_type == "BarRating" ||
          this.$utils.getMetricByReferenceName(metric_ref).input_type == "Boolean" ||
          this.$utils.getMetricByReferenceName(metric_ref).input_type == "Date"
        )

        if (metric_ref == "_parent" || metric_ref == "parent__id") {
          this.record_metrics.push(
            this.$utils.clone({
              ...this.$utils.getMetricByReferenceName(metric_ref),
              value: this.$utils.clone(this.component_record.parent),
              click_to_edit
            })
          )
        } else if (metric_ref == "top_parent") {
          this.record_metrics.push(
            this.$utils.clone({
              ...this.$utils.getMetricByReferenceName(metric_ref),
              value: this.$utils.clone(this.component_record.top_parent) || "",
              click_to_edit
            })
          )
        } else {
          this.record_metrics.push({
            ...this.$utils.getMetricByReferenceName(metric_ref),
            value: this.component_record.values[metric_ref],
            click_to_edit
          })
        }
      })

      setTimeout(() => {
        this.prepareValidationSchema()
      }, 2000)
    }
  }
}
</script>
